import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProtectedRoute from '../../components/ProtectedRoute';
import { db } from '../../firebase';
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  Timestamp,
  where,
  writeBatch
} from 'firebase/firestore';
import SectionAd from '../../components/ads/SectionAd';
import {
  ADMIN_UPDATE_RETENTION_DAYS,
  ADMIN_UPDATES_COLLECTION
} from '../../utils/adminUpdates';


const getDateValue = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getUserFactionIds = (user) => {
  const ids = [
    user?.favoriteFactionId,
    user?.factionId,
    ...(Array.isArray(user?.factionIds) ? user.factionIds : []),
    user?.roadToFactionId,
  ].filter(Boolean);

  return [...new Set(ids)];
};

const getUserFactionCount = (user) => getUserFactionIds(user).length;

const getUserCreatedAt = (user, createdAtByUid = {}) => (
  getDateValue(user?.createdAt) || createdAtByUid[user?.id] || null
);

const hasActiveNotifications = (user) => (
  user?.notificationsEnabled === true &&
  typeof user?.fcmToken === 'string' &&
  user.fcmToken.trim().length > 0
);

export default function AdminPage() {
  const navigate = useNavigate();
  const [updates, setUpdates] = useState([]);
  const [updatesState, setUpdatesState] = useState({
    loading: true,
    clearing: false,
    message: '',
    error: '',
  });
  const [cleanupState, setCleanupState] = useState({
    loading: false,
    message: '',
    error: '',
  });
  const [counts, setCounts] = useState({
    wrestlers: null,
    rosters: null,
    factions: null,
    shows: null,
    users: null,
    teams: null,
    admin: null,
    news: null,
    pollResponses: null
  });
  const [insights, setInsights] = useState({
    newUsers7: 0,
    newUsers30: 0,
    activeNotifications: 0,
    factionDistribution: [],
    modeCounts: {},
  });

  const openAdminDestination = useCallback((destination, external = false) => {
    if (!destination) return;

    if (external) {
      window.open(destination, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(destination);
  }, [navigate]);

  const fetchCounts = useCallback(async () => {
    const collections = ['wrestlers', 'rosters', 'factions', 'shows', 'teams', 'news', 'pollResponses'];
    const newCounts = {};
    for (const col of collections) {
      try {
        const snap = await getCountFromServer(collection(db, col));
        newCounts[col] = snap.data().count;
      } catch (e) {
        newCounts[col] = 'Errore';
      }
    }
    // Conta utenti e admin
    try {
      const userSnap = await getDocs(collection(db, 'users'));
      const usersArr = userSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      newCounts.users = usersArr.length;

      const [usernamesSnap, rostersSnap, factionsSnap] = await Promise.all([
        getDocs(collection(db, 'usernames')),
        getDocs(collection(db, 'rosters')),
        getDocs(collection(db, 'factions')),
      ]);

      const usernameCreatedAtByUid = {};
      usernamesSnap.forEach((docSnap) => {
        const data = docSnap.data();
        if (data?.uid) {
          usernameCreatedAtByUid[data.uid] = getDateValue(data.createdAt);
        }
      });

      const now = new Date();
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(now.getDate() - 7);
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(now.getDate() - 30);

      let newUsers7 = 0;
      let newUsers30 = 0;
      let activeNotifications = 0;
      usersArr.forEach((user) => {
        const createdAt = getUserCreatedAt(user, usernameCreatedAtByUid);
        if (createdAt) {
          if (createdAt >= thirtyDaysAgo) newUsers30 += 1;
          if (createdAt >= sevenDaysAgo) newUsers7 += 1;
        }
        if (hasActiveNotifications(user)) activeNotifications += 1;
      });

      const factionDistributionMap = {
        '0 fazioni': 0,
        '1 fazione': 0,
        '2 fazioni': 0,
        '3+ fazioni': 0,
      };

      usersArr.forEach((user) => {
        const factionCount = getUserFactionCount(user);

        if (factionCount === 0) factionDistributionMap['0 fazioni'] += 1;
        else if (factionCount === 1) factionDistributionMap['1 fazione'] += 1;
        else if (factionCount === 2) factionDistributionMap['2 fazioni'] += 1;
        else factionDistributionMap['3+ fazioni'] += 1;
      });

      const rostersMap = {};
      rostersSnap.forEach((docSnap) => {
        rostersMap[docSnap.id] = docSnap.data();
      });

      const modeCounts = {
        Competitivo: 0,
        Indipendente: 0,
        'Road To': 0,
        GM: 0,
      };

      factionsSnap.forEach((docSnap) => {
        const faction = docSnap.data();
        const roster = rostersMap[faction.rosterId] || null;
        const mode = roster?.isGM
          ? 'GM'
          : roster?.gameType || faction.gameType || 'Competitivo';

        if (modeCounts[mode] !== undefined) {
          modeCounts[mode] += 1;
        } else {
          modeCounts.Competitivo += 1;
        }
      });

      setInsights({
        newUsers7,
        newUsers30,
        activeNotifications,
        factionDistribution: Object.entries(factionDistributionMap),
        modeCounts,
      });
    } catch (e) {
      newCounts.users = 'Errore';
    }
    setCounts(newCounts);
  }, []);

  const cleanupExpiredUpdates = useCallback(async () => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - ADMIN_UPDATE_RETENTION_DAYS);

    const expiredQuery = query(
      collection(db, ADMIN_UPDATES_COLLECTION),
      where('createdAt', '<', Timestamp.fromDate(cutoffDate))
    );
    const expiredSnap = await getDocs(expiredQuery);
    if (expiredSnap.empty) return 0;

    let pendingBatch = writeBatch(db);
    let pendingWrites = 0;
    let deletedCount = 0;

    for (const updateDoc of expiredSnap.docs) {
      pendingBatch.delete(updateDoc.ref);
      pendingWrites += 1;
      deletedCount += 1;

      if (pendingWrites === 450) {
        await pendingBatch.commit();
        pendingBatch = writeBatch(db);
        pendingWrites = 0;
      }
    }

    if (pendingWrites > 0) {
      await pendingBatch.commit();
    }

    return deletedCount;
  }, []);

  const fetchUpdates = useCallback(async () => {
    setUpdatesState(prev => ({ ...prev, loading: true, error: '' }));

    try {
      await cleanupExpiredUpdates();
      const updatesQuery = query(
        collection(db, ADMIN_UPDATES_COLLECTION),
        orderBy('createdAt', 'desc'),
        limit(80)
      );
      const updatesSnap = await getDocs(updatesQuery);
      setUpdates(updatesSnap.docs.map(updateDoc => ({
        id: updateDoc.id,
        ...updateDoc.data()
      })));
      setUpdatesState(prev => ({ ...prev, loading: false, error: '' }));
    } catch (error) {
      console.error('Errore caricamento aggiornamenti:', error);
      setUpdatesState(prev => ({
        ...prev,
        loading: false,
        error: 'Errore durante il caricamento degli aggiornamenti.'
      }));
    }
  }, [cleanupExpiredUpdates]);

  useEffect(() => {
    fetchCounts();
    fetchUpdates();
  }, [fetchCounts, fetchUpdates]);

  const handleClearUpdates = async () => {
    if (!window.confirm('Svuotare tutti gli aggiornamenti registrati?')) return;

    setUpdatesState(prev => ({ ...prev, clearing: true, message: '', error: '' }));

    try {
      const updatesSnap = await getDocs(collection(db, ADMIN_UPDATES_COLLECTION));
      await Promise.all(updatesSnap.docs.map(updateDoc => deleteDoc(updateDoc.ref)));
      setUpdates([]);
      setUpdatesState({
        loading: false,
        clearing: false,
        message: 'Aggiornamenti svuotati.',
        error: '',
      });
    } catch (error) {
      console.error('Errore svuotamento aggiornamenti:', error);
      setUpdatesState(prev => ({
        ...prev,
        clearing: false,
        error: 'Errore durante lo svuotamento degli aggiornamenti.'
      }));
    }
  };

  const formatUpdateDate = (value) => {
    const date = getDateValue(value);
    if (!date) return '-';
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCleanUserFactionFields = async () => {
    const confirmed = window.confirm(
      'Confermi la rimozione dei campi factionId, favoriteFactionId e roadToFactionId da tutti gli utenti? L\'operazione aggiornera i documenti users che li contengono.'
    );

    if (!confirmed) return;

    setCleanupState({ loading: true, message: '', error: '' });

    try {
      const usersSnap = await getDocs(collection(db, 'users'));
      let pendingBatch = writeBatch(db);
      let pendingWrites = 0;
      let cleanedUsers = 0;
      const fieldsToRemove = ['factionId', 'favoriteFactionId', 'roadToFactionId'];

      for (const userDoc of usersSnap.docs) {
        const data = userDoc.data();
        const hasFieldToRemove = fieldsToRemove.some((field) => (
          Object.prototype.hasOwnProperty.call(data, field)
        ));

        if (!hasFieldToRemove) continue;

        pendingBatch.update(doc(db, 'users', userDoc.id), {
          factionId: deleteField(),
          favoriteFactionId: deleteField(),
          roadToFactionId: deleteField(),
        });
        pendingWrites += 1;
        cleanedUsers += 1;

        if (pendingWrites === 450) {
          await pendingBatch.commit();
          pendingBatch = writeBatch(db);
          pendingWrites = 0;
        }
      }

      if (pendingWrites > 0) {
        await pendingBatch.commit();
      }

      setCleanupState({
        loading: false,
        message: cleanedUsers === 0
          ? 'Nessun utente aveva campi fazione legacy da rimuovere.'
          : `Pulizia completata: aggiornati ${cleanedUsers} utenti.`,
        error: '',
      });
      await fetchCounts();
    } catch (error) {
      console.error('Errore pulizia campi fazione utenti:', error);
      setCleanupState({
        loading: false,
        message: '',
        error: 'Errore durante la pulizia dei record utenti. Riprova.',
      });
    }
  };

  const mostPlayedMode = useMemo(() => {
    const entries = Object.entries(insights.modeCounts || {});
    if (entries.length === 0) return null;
    return entries.sort((a, b) => b[1] - a[1])[0];
  }, [insights.modeCounts]);

  return (
    <ProtectedRoute>

      <div className="admin-page container py-4">

        <div className="row g-3">
          {[
            {
              title: "Gestione Show",
              icon: "bi-tv-fill",
              color: "warning",
              path: "/admin/add-shows",
              count: counts.shows,
              countLabel: 'Show'
            },
            {
              title: "Gestione Wrestler",
              icon: "bi-person-bounding-box",
              color: "success",
              path: "/admin/add-wrestlers",
              count: counts.wrestlers,
              countLabel: 'Wrestlers'
            },
            {
              title: "Gestione Roster",
              icon: "bi-collection-fill",
              color: "info",
              path: "/admin/add-roster",
              count: counts.rosters,
              countLabel: 'Rosters'
            },
            {
              title: "Gestione News",
              icon: "bi-newspaper",
              color: "primary",
              path: "/admin/add-news",
              count: counts.news,
              countLabel: 'News'
            },
            {
              title: "Gestione Utenti",
              icon: "bi-person-lines-fill",
              color: "secondary",
              path: "/admin/users",
              count: counts.users,
              countLabel: `Utenti `
            },
            {
              title: "Utenti Premium",
              icon: "bi-stars",
              color: "warning",
              path: "/admin/premium-users",
              description: 'Gestisci accessi premium',
            },
            {
              title: "Gestione Teams",
              icon: "bi-people-fill",
              color: "danger",
              path: "/admin/add-teams",
              count: counts.teams,
              countLabel: 'Teams'
            },
            {
              title: "Gestione Fazioni",
              icon: "bi-people-fill",
              color: "primary",
              path: "/admin/add-factions",
              count: counts.factions,
              countLabel: 'Fazioni'
            },
            /*{
              title: "Risposte Sondaggio",
              icon: "bi-bar-chart-line-fill",
              color: "info",
              path: "/admin/add-response",
              count: counts.pollResponses,
              countLabel: 'Risposte'
            },*/
            {
              title: "Guadagni Pubblicità",
              icon: "bi-cash-coin",
              color: "success",
              path: "https://user.hilltopads.com/",
              description: 'Apri HilltopAds',
              external: true,
            },
          ].map((item, index) => (
            <div key={index} className="col-md-6 col-lg-3">
              <div
                className={`card border-0 shadow-sm hover-shadow transition-all h-100 admin-dashboard-card`}
                onClick={() => openAdminDestination(item.path, item.external)}
                role="button"
              >
                <div className="card-body text-center p-4">
                  <div className={`icon-circle admin-icon-circle text-${item.color} mx-auto`}>
                    <i className={`bi ${item.icon} fs-3`}></i>
                  </div>
                  <h5 className="card-title mb-0">{item.title}</h5>
                  {item.description ? (
                    <span className="text-muted fs-6">{item.description}</span>
                  ) : (
                    <span className="text-muted fs-6">
                      {item.count !== null && item.count !== undefined ? item.count : <span className="spinner-border spinner-border-sm" />} {item.countLabel}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <SectionAd placement="adminPage" className="d-flex justify-content-center my-5" />
        <div className="row g-3 mt-4">
          <div className="col-12 col-lg-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h5 className="fw-bold mb-3">Nuovi utenti</h5>
                <button
                  type="button"
                  className="btn w-100 d-flex justify-content-between align-items-center rounded p-3 bg-light bg-opacity-10 mb-2 text-start border-0"
                  onClick={() => navigate('/admin/users?preset=recent-7')}
                >
                  <span>Ultimi 7 giorni</span>
                  <strong>{insights.newUsers7}</strong>
                </button>
                <button
                  type="button"
                  className="btn w-100 d-flex justify-content-between align-items-center rounded p-3 bg-light bg-opacity-10 text-start border-0"
                  onClick={() => navigate('/admin/users?preset=recent-30')}
                >
                  <span>Ultimi 30 giorni</span>
                  <strong>{insights.newUsers30}</strong>
                </button>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h5 className="fw-bold mb-3">Notifiche attive</h5>
                <button
                  type="button"
                  className="btn w-100 d-flex justify-content-between align-items-center rounded p-3 bg-light bg-opacity-10 text-start border-0"
                  onClick={() => navigate('/admin/users?preset=notifications-active')}
                >
                  <span>Utenti con token</span>
                  <strong>{insights.activeNotifications}</strong>
                </button>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h5 className="fw-bold mb-3">Utenti per numero di fazioni</h5>
                <div className="d-flex flex-column gap-2">
                  {insights.factionDistribution.map(([label, value]) => (
                    <button
                      key={label}
                      type="button"
                      className="btn w-100 d-flex justify-content-between align-items-center rounded p-3 bg-light bg-opacity-10 text-start border-0"
                      onClick={() => {
                        const presetByLabel = {
                          '0 fazioni': 'factions-0',
                          '1 fazione': 'factions-1',
                          '2 fazioni': 'factions-2',
                          '3+ fazioni': 'factions-3plus',
                        };
                        navigate(`/admin/users?preset=${presetByLabel[label]}`);
                      }}
                    >
                      <span>{label}</span>
                      <strong>{value}</strong>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h5 className="fw-bold mb-3">Modalità più giocata</h5>
                <div className="rounded p-3 bg-light bg-opacity-10 mb-3">
                  <div className="small text-muted mb-1">Top modalità</div>
                  <div className="fw-bold fs-4">
                    {mostPlayedMode ? `${mostPlayedMode[0]} (${mostPlayedMode[1]})` : 'N/D'}
                  </div>
                </div>
                <div className="d-flex flex-column gap-2">
                  {Object.entries(insights.modeCounts).map(([label, value]) => (
                    <div key={label} className="d-flex justify-content-between align-items-center">
                      <span>{label}</span>
                      <strong>{value}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm admin-list-card mt-4">
          <div className="card-header bg-light py-3">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
              <div>
                <h5 className="fw-bold mb-1">
                  <i className="bi bi-clock-history me-2"></i>
                  Aggiornamenti
                </h5>
                <small className="text-muted">
                  Record pubblici salvati nelle ultime {ADMIN_UPDATE_RETENTION_DAYS} giornate
                </small>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-outline-info btn-sm"
                  onClick={fetchUpdates}
                  disabled={updatesState.loading || updatesState.clearing}
                >
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  Ricarica
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={handleClearUpdates}
                  disabled={updatesState.loading || updatesState.clearing || updates.length === 0}
                >
                  {updatesState.clearing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Svuotamento...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-trash3 me-1"></i>
                      Svuota aggiornamenti
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
          <div className="card-body">
            {updatesState.message && (
              <div className="alert alert-success py-2">{updatesState.message}</div>
            )}
            {updatesState.error && (
              <div className="alert alert-danger py-2">{updatesState.error}</div>
            )}
            {updatesState.loading ? (
              <div className="text-center py-4">
                <span className="spinner-border text-primary" />
              </div>
            ) : updates.length === 0 ? (
              <div className="text-center py-4 text-muted">
                <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                Nessun aggiornamento registrato.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Data e ora</th>
                      <th>Tipo</th>
                      <th>Azione</th>
                      <th>Record</th>
                      <th className="d-none d-lg-table-cell">Dettagli</th>
                    </tr>
                  </thead>
                  <tbody>
                    {updates.map(update => (
                      <tr key={update.id}>
                        <td className="small text-nowrap">{formatUpdateDate(update.createdAt)}</td>
                        <td>
                          <span className="badge bg-primary">{update.type || '-'}</span>
                        </td>
                        <td>{update.action || '-'}</td>
                        <td className="fw-semibold">{update.title || '-'}</td>
                        <td className="d-none d-lg-table-cell text-muted">
                          {update.description || update.actorName || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

      </div>
    </ProtectedRoute>
  );
}
